import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { UserProfilePopup } from './UserProfilePopup';
import { ThreadPanel } from './ThreadPanel';
import { Hash, Send, MoreHorizontal, Reply, Edit2, Trash2, Pin, Search, X, Smile, PinOff, CornerUpRight, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🔥', '🎮', '👏', '💯', '⭐'];
const PERM_ADMIN = 1 << 8;
const PERM_USE_MEDIA = 1 << 9;
const EMOJI_REGEX = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
const GIF_REGEX = /(https?:\/\/\S+\.(gif|webp)|tenor\.com|giphy\.com)/i;
const containsMedia = (text) => Boolean(text && (EMOJI_REGEX.test(text) || GIF_REGEX.test(text)));

export const ChatArea = () => {
  const { currentChannel, fetchChannels, fetchMembers, userPerms } = useApp();
  const { user, token } = useAuth();
  const { on } = useSocket();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [profileUserId, setProfileUserId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showPinned, setShowPinned] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showEmojiFor, setShowEmojiFor] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [mediaError, setMediaError] = useState('');
  const canUseMedia = Boolean(userPerms & PERM_USE_MEDIA) || Boolean(userPerms & PERM_ADMIN);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef({});

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'
  }), [token]);

  const fetchMessages = useCallback(async () => {
    if (!currentChannel || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setMessages(await res.json());
      // Mark as read
      fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/read`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentChannel, token]);

  useEffect(() => {
    fetchMessages();
    setReplyTo(null);
    setEditingMsg(null);
    setShowSearch(false);
    setShowPinned(false);
  }, [fetchMessages]);

  // WebSocket events
  useEffect(() => {
    if (!on) return;
    const unsubs = [
      on('MESSAGE_CREATE', (e) => {
        if (e.data.channel_id === currentChannel?.channel_id) {
          setMessages(prev => {
            // Skip if already exists (optimistic or duplicate)
            if (prev.some(m => m.message_id === e.data.message_id)) return prev;
            // Replace temp optimistic msg from same author with same content
            const optimistic = prev.find(m => m._optimistic && m.user_id === e.data.user_id && m.content === e.data.content);
            if (optimistic) return prev.map(m => m.message_id === optimistic.message_id ? e.data : m);
            return [...prev, e.data];
          });
          fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/read`, {
            method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
          }).catch(() => {});
        }
      }),
      on('MESSAGE_UPDATE', (e) => {
        if (e.data.channel_id === currentChannel?.channel_id) {
          setMessages(prev => prev.map(m => m.message_id === e.data.message_id
            ? { ...m, content: e.data.content, edited: true, edited_at: e.data.edited_at } : m));
        }
      }),
      on('MESSAGE_DELETE', (e) => {
        if (e.data.channel_id === currentChannel?.channel_id) {
          setMessages(prev => prev.filter(m => m.message_id !== e.data.message_id));
        }
      }),
      on('REACTION_ADD', (e) => {
        if (e.data.channel_id === currentChannel?.channel_id) {
          setMessages(prev => prev.map(m => {
            if (m.message_id !== e.data.message_id) return m;
            const reactions = { ...m.reactions };
            if (!reactions[e.data.emoji]) reactions[e.data.emoji] = [];
            if (!reactions[e.data.emoji].includes(e.data.user_id)) {
              reactions[e.data.emoji] = [...reactions[e.data.emoji], e.data.user_id];
            }
            return { ...m, reactions };
          }));
        }
      }),
      on('REACTION_REMOVE', (e) => {
        if (e.data.channel_id === currentChannel?.channel_id) {
          setMessages(prev => prev.map(m => {
            if (m.message_id !== e.data.message_id) return m;
            const reactions = { ...m.reactions };
            if (reactions[e.data.emoji]) {
              reactions[e.data.emoji] = reactions[e.data.emoji].filter(uid => uid !== e.data.user_id);
              if (reactions[e.data.emoji].length === 0) delete reactions[e.data.emoji];
            }
            return { ...m, reactions };
          }));
        }
      }),
      on('TYPING_START', (e) => {
        if (e.data.channel_id === currentChannel?.channel_id && e.data.user_id !== user?.user_id) {
          setTypingUsers(prev => prev.find(u => u.user_id === e.data.user_id) ? prev : [...prev, e.data]);
          if (typingTimeoutRef.current[e.data.user_id]) clearTimeout(typingTimeoutRef.current[e.data.user_id]);
          typingTimeoutRef.current[e.data.user_id] = setTimeout(() => {
            setTypingUsers(prev => prev.filter(u => u.user_id !== e.data.user_id));
          }, 3000);
        }
      })
    ];
    return () => unsubs.forEach(u => u?.());
  }, [on, currentChannel, user, token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentChannel) return;
    const content = newMessage;
    if (containsMedia(content) && !canUseMedia) {
      setMediaError('Nu ai permisiunea pentru emoji/GIF/sticker.');
      return;
    }
    const reply = replyTo?.message_id || null;
    const replyMsg = replyTo;
    setNewMessage('');
    setReplyTo(null);
    setMediaError('');

    // Optimistic: add message to UI instantly
    const tempMsg = {
      message_id: `temp_${Date.now()}`,
      channel_id: currentChannel.channel_id,
      content,
      user_id: user.user_id,
      user: { user_id: user.user_id, username: user.username, display_name: user.display_name, avatar: user.avatar },
      reply_to: reply,
      reply_preview: replyMsg ? { message_id: replyMsg.message_id, content: replyMsg.content, user: replyMsg.user } : null,
      reactions: {},
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const res = await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/messages`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ content, reply_to: reply })
      });
      if (res.ok) {
        const real = await res.json();
        // Replace temp message with real one, keep user data
        setMessages(prev => prev.map(m => m.message_id === tempMsg.message_id ? { ...real, user: real.user || tempMsg.user } : m));
      } else {
        setMessages(prev => prev.filter(m => m.message_id !== tempMsg.message_id));
        const err = await res.json().catch(() => ({}));
        setMediaError(err.detail || 'Nu ai permisiunea pentru emoji/GIF/sticker.');
      }
    } catch (err) {
      // Remove failed optimistic message
      setMessages(prev => prev.filter(m => m.message_id !== tempMsg.message_id));
      console.error(err);
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim() || !editingMsg) return;
    await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/messages/${editingMsg.message_id}`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ content: editContent })
    });
    setEditingMsg(null);
    setEditContent('');
  };

  const openThread = async (msg) => {
    // Create thread if doesn't exist
    let threadId = msg.thread_id;
    if (!threadId) {
      const res = await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/messages/${msg.message_id}/thread`, {
        method: 'POST', headers: authHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        threadId = data.thread_id;
        setMessages(prev => prev.map(m => m.message_id === msg.message_id ? { ...m, thread_id: threadId } : m));
      }
    }
    if (threadId) setActiveThread({ threadId, parentMessage: msg });
  };

  const handleDelete = async (msgId) => {
    await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/messages/${msgId}`, {
      method: 'DELETE', headers: authHeaders()
    });
  };

  const handleReaction = async (msgId, emoji) => {
    if (!canUseMedia) {
      setMediaError('Nu ai permisiunea pentru emoji/GIF/sticker.');
      setShowEmojiFor(null);
      return;
    }
    const msg = messages.find(m => m.message_id === msgId);
    const hasReacted = msg?.reactions?.[emoji]?.includes(user.user_id);
    if (hasReacted) {
      await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/messages/${msgId}/reactions/${encodeURIComponent(emoji)}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
    } else {
      await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/messages/${msgId}/reactions/${encodeURIComponent(emoji)}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    }
    setShowEmojiFor(null);
  };

  const handlePin = async (msgId, pinned) => {
    const method = pinned ? 'DELETE' : 'POST';
    await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/messages/${msgId}/pin`, {
      method, headers: { 'Authorization': `Bearer ${token}` }
    });
    setMessages(prev => prev.map(m => m.message_id === msgId ? { ...m, pinned: !pinned } : m));
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    const res = await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/search?q=${encodeURIComponent(searchQuery)}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) setSearchResults(await res.json());
  };

  const fetchPinned = async () => {
    const res = await fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/pins`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) setPinnedMessages(await res.json());
  };

  const sendTyping = useCallback(() => {
    if (!currentChannel || !token) return;
    fetch(`${API_URL}/api/channels/${currentChannel.channel_id}/typing`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
    }).catch(() => {});
  }, [currentChannel, token]);

  if (!currentChannel) return null;

  const grouped = groupMessagesByDate(messages);

  return (
    <div className="flex-1 flex min-h-0 min-w-0">
    <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-[#0b0d10]" data-testid="chat-area">
      {/* Channel header */}
      <div className="h-12 px-4 flex items-center gap-2 border-b border-white/5 flex-shrink-0">
        <Hash className="h-4 w-4 text-zinc-500 flex-shrink-0" />
        <span className="font-semibold text-sm text-white flex-1 min-w-0 truncate" data-testid="chat-channel-name">{currentChannel.name}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => { setShowPinned(true); fetchPinned(); }}
            className="p-1.5 text-zinc-500 hover:text-white hover:bg-white/5 rounded transition-colors" data-testid="show-pins-btn">
            <Pin className="h-4 w-4" />
          </button>
          <button onClick={() => setShowSearch(!showSearch)}
            className="p-1.5 text-zinc-500 hover:text-white hover:bg-white/5 rounded transition-colors" data-testid="show-search-btn">
            <Search className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Search bar */}
      {showSearch && (
        <div className="px-4 py-2 border-b border-white/5 flex gap-2 bg-[#0e1015]" data-testid="search-bar">
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search messages..." className="bg-[#0b0d10] border-white/5 text-sm h-8 flex-1"
            onKeyDown={e => e.key === 'Enter' && handleSearch()} data-testid="search-input" />
          <Button size="sm" onClick={handleSearch} className="bg-cyan-600 hover:bg-cyan-700 h-8 text-xs">Search</Button>
          <button onClick={() => { setShowSearch(false); setSearchResults([]); setSearchQuery(''); }} className="text-zinc-500 hover:text-white p-1"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Search results overlay */}
      {searchResults.length > 0 && (
        <div className="px-4 py-2 border-b border-white/5 bg-[#0e1015] max-h-48 overflow-y-auto" data-testid="search-results">
          <p className="text-[11px] text-zinc-500 mb-1">{searchResults.length} results</p>
          {searchResults.map(m => (
            <div key={m.message_id} className="flex items-start gap-2 px-2 py-1.5 text-sm hover:bg-white/5 rounded">
              <span className="text-zinc-400 font-medium text-xs">{m.user?.display_name || m.user?.username}</span>
              <span className="text-zinc-200 text-xs flex-1 truncate">{m.content}</span>
              <span className="text-[10px] text-zinc-600">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-2">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="h-6 w-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-16 text-center">
            <Hash className="h-12 w-12 text-zinc-700 mb-3" />
            <p className="text-zinc-400 text-sm">{t('app.noMessages')}</p>
          </div>
        ) : (
          grouped.map(group => (
            <div key={group.date}>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-white/5" />
                <span className="text-[11px] text-zinc-500 font-medium">{group.date}</span>
                <div className="flex-1 h-px bg-white/5" />
              </div>
              {group.messages.map((msg, i) => {
                const prevMsg = i > 0 ? group.messages[i - 1] : null;
                const compact = prevMsg && prevMsg.user_id === msg.user_id && !msg.reply_to && timeDiffMinutes(prevMsg.created_at, msg.created_at) < 5;
                return (
                  <MessageBubble
                    key={msg.message_id} message={msg} compact={compact} currentUserId={user?.user_id}
                    onUserClick={() => setProfileUserId(msg.user_id)}
                    onReply={() => { setReplyTo(msg); inputRef.current?.focus(); }}
                    onEdit={() => { setEditingMsg(msg); setEditContent(msg.content); }}
                    onDelete={() => handleDelete(msg.message_id)}
                    onReaction={(emoji) => handleReaction(msg.message_id, emoji)}
                    onPin={() => handlePin(msg.message_id, msg.pinned)}
                    onThread={() => openThread(msg)}
                    showEmojiFor={showEmojiFor}
                    setShowEmojiFor={setShowEmojiFor}
                    canUseMedia={canUseMedia}
                    onMediaBlocked={() => setMediaError('Nu ai permisiunea pentru emoji/GIF/sticker.')}
                  />
                );
              })}
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </ScrollArea>

      {/* Typing indicator */}
      {typingUsers.length > 0 && (
        <div className="px-4 py-1 text-xs text-zinc-500 flex items-center gap-1" data-testid="typing-indicator">
          <span className="flex gap-0.5">
            <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
            <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
            <span className="w-1.5 h-1.5 bg-zinc-500 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
          </span>
          <span>{typingUsers.map(u => u.username).join(', ')} typing...</span>
        </div>
      )}

      {mediaError && (
        <div className="px-4 py-1 text-xs text-red-400 bg-red-500/10 border-t border-white/5" data-testid="media-permission-alert">
          {mediaError}
        </div>
      )}

      {/* Reply preview */}
      {replyTo && (
        <div className="px-4 py-1.5 bg-[#0e1015] border-t border-white/5 flex items-center gap-2" data-testid="reply-preview">
          <Reply className="h-3.5 w-3.5 text-cyan-400" />
          <span className="text-xs text-cyan-400 font-medium">Replying to {replyTo.user?.display_name || replyTo.user?.username || 'user'}</span>
          <span className="text-xs text-zinc-500 truncate flex-1">{replyTo.content?.slice(0, 60)}</span>
          <button onClick={() => setReplyTo(null)} className="text-zinc-500 hover:text-white"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* Edit mode */}
      {editingMsg && (
        <div className="px-4 py-2 bg-[#0e1015] border-t border-white/5" data-testid="edit-mode">
          <div className="flex items-center gap-2 mb-1">
            <Edit2 className="h-3.5 w-3.5 text-yellow-400" />
            <span className="text-xs text-yellow-400 font-medium">Editing message</span>
            <button onClick={() => setEditingMsg(null)} className="ml-auto text-zinc-500 hover:text-white"><X className="h-3.5 w-3.5" /></button>
          </div>
          <div className="flex gap-2">
            <input value={editContent} onChange={e => setEditContent(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleEdit(); if (e.key === 'Escape') setEditingMsg(null); }}
              className="flex-1 bg-[#12141a] rounded-lg px-3 py-2 text-sm text-white border border-white/5 outline-none focus:border-yellow-500/30"
              data-testid="edit-input" autoFocus />
            <Button size="sm" onClick={handleEdit} className="bg-yellow-600 hover:bg-yellow-700 text-xs h-9" data-testid="save-edit-btn">Save</Button>
          </div>
        </div>
      )}

      {/* Message input */}
      {!editingMsg && (
        <form onSubmit={sendMessage} className="p-3 flex-shrink-0" data-testid="message-form">
          <div className="flex items-center gap-2 bg-[#12141a] rounded-lg px-3 border border-white/5 focus-within:border-cyan-500/30 transition-colors">
            <input ref={inputRef} type="text" value={newMessage}
              onChange={e => {
                const value = e.target.value;
                setNewMessage(value);
                if (mediaError && (canUseMedia || !containsMedia(value))) setMediaError('');
                sendTyping();
              }}
              placeholder={`${t('app.typeMessage')}`}
              className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none"
              data-testid="message-input" />
            <button type="submit" disabled={!newMessage.trim()}
              className="text-zinc-500 hover:text-cyan-400 disabled:opacity-30 transition-colors p-1"
              data-testid="send-message-btn">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </form>
      )}

      <UserProfilePopup userId={profileUserId} open={!!profileUserId} onClose={() => setProfileUserId(null)} />

      {/* Pinned messages dialog */}
      <Dialog open={showPinned} onOpenChange={setShowPinned}>
        <DialogContent className="bg-[#12141a] border-white/10 text-white max-w-md max-h-[60vh] overflow-y-auto" data-testid="pinned-dialog">
          <DialogTitle className="text-lg font-semibold">Pinned Messages</DialogTitle>
          {pinnedMessages.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">No pinned messages</p>
          ) : (
            pinnedMessages.map(m => (
              <div key={m.message_id} className="flex gap-2 px-2 py-2 border-b border-white/5 last:border-0">
                <Avatar className="h-7 w-7 flex-shrink-0">
                  <AvatarImage src={m.user?.avatar} />
                  <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-[10px]">{(m.user?.display_name || m.user?.username || '?')[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-semibold text-white">{m.user?.display_name || m.user?.username}</span>
                    <span className="text-[10px] text-zinc-600">{new Date(m.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-xs text-zinc-300">{m.content}</p>
                </div>
              </div>
            ))
          )}
        </DialogContent>
      </Dialog>
    </div>
    {/* Thread panel */}
    {activeThread && (
      <ThreadPanel
        threadId={activeThread.threadId}
        parentMessage={activeThread.parentMessage}
        onClose={() => setActiveThread(null)}
      />
    )}
    </div>
  );
};

// Message bubble with context menu
const MessageBubble = ({ message, compact, currentUserId, onUserClick, onReply, onEdit, onDelete, onReaction, onPin, onThread, showEmojiFor, setShowEmojiFor, canUseMedia, onMediaBlocked }) => {
  const timeStr = new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const userInfo = message.user || {};
  const displayName = userInfo.nickname || userInfo.display_name || userInfo.username || 'Unknown';
  const roleColor = getRoleColor(userInfo.roles);
  const isOwn = message.user_id === currentUserId;
  const reactions = message.reactions || {};
  const embeds = extractEmbeds(message.content);

  const replyRef = message.reply_to;

  return (
    <div className="group relative overflow-visible" data-testid={`message-${message.message_id}`}>
      {/* Reply reference */}
      {replyRef && !compact && (
        <div className="flex items-center gap-1.5 pl-12 mb-0.5">
          <CornerUpRight className="h-3 w-3 text-zinc-600 rotate-180 scale-x-[-1]" />
          <span className="text-[11px] text-zinc-500">{replyRef.user?.display_name || replyRef.user?.username}</span>
          <span className="text-[11px] text-zinc-600 truncate max-w-xs">{replyRef.content}</span>
        </div>
      )}

      <div className={`flex gap-3 px-2 py-1 hover:bg-white/[0.02] rounded relative ${compact ? 'pl-12' : ''}`}>
        {/* Action bar (hover) */}
        <div className="absolute -top-3 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 flex gap-0.5 bg-[#1a1d23] border border-white/10 rounded-md px-0.5 py-0.5 shadow-lg" data-testid={`msg-actions-${message.message_id}`}>
          <button
            onClick={() => {
              if (!canUseMedia) {
                onMediaBlocked?.();
                return;
              }
              setShowEmojiFor(showEmojiFor === message.message_id ? null : message.message_id);
            }}
            disabled={!canUseMedia}
            className={`p-1 rounded transition-colors ${canUseMedia ? 'text-zinc-400 hover:text-white hover:bg-white/10' : 'text-zinc-600 opacity-50 cursor-not-allowed'}`}
            title={canUseMedia ? "React" : "Nu ai permisiunea pentru emoji/GIF/sticker"}
            data-testid={`react-btn-${message.message_id}`}
          >
            <Smile className="h-3.5 w-3.5" />
          </button>
          <button onClick={onReply} className="p-1 text-zinc-400 hover:text-white hover:bg-white/10 rounded" title="Reply" data-testid={`reply-btn-${message.message_id}`}>
            <Reply className="h-3.5 w-3.5" />
          </button>
          {isOwn && (
            <button onClick={onEdit} className="p-1 text-zinc-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded" title="Edit" data-testid={`edit-btn-${message.message_id}`}>
              <Edit2 className="h-3.5 w-3.5" />
            </button>
          )}
          {isOwn && (
            <button onClick={onDelete} className="p-1 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded" title="Delete" data-testid={`delete-btn-${message.message_id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          <button onClick={onPin} className="p-1 text-zinc-400 hover:text-white hover:bg-white/10 rounded"
            title={message.pinned ? "Unpin" : "Pin"} data-testid={`pin-btn-${message.message_id}`}>
            {message.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
          </button>
          <button onClick={onThread} className="p-1 text-zinc-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded" title="Thread" data-testid={`thread-btn-${message.message_id}`}>
            <MessageSquare className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Emoji picker */}
        {showEmojiFor === message.message_id && canUseMedia && (
          <div className="absolute -top-10 right-2 z-20 bg-[#1a1d23] border border-white/10 rounded-lg p-1.5 flex gap-1 shadow-xl" data-testid={`emoji-picker-${message.message_id}`}>
            {QUICK_EMOJIS.map(e => (
              <button key={e} onClick={() => onReaction(e)}
                className="h-7 w-7 flex items-center justify-center hover:bg-white/10 rounded text-sm transition-transform hover:scale-125"
                data-testid={`emoji-${e}`}>
                {e}
              </button>
            ))}
          </div>
        )}

        {/* Avatar */}
        {!compact && (
          <button onClick={onUserClick} className="flex-shrink-0 mt-0.5">
            <Avatar className="h-9 w-9">
              <AvatarImage src={userInfo.avatar} />
              <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">{displayName[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
          </button>
        )}

        <div className="min-w-0 flex-1">
          {!compact && (
            <div className="flex items-baseline gap-2">
              <button onClick={onUserClick} className="text-sm font-semibold hover:underline" style={{ color: roleColor }}>
                {displayName}
              </button>
              <span className="text-[10px] text-zinc-600">{timeStr}</span>
              {message.edited && <span className="text-[10px] text-zinc-600 italic">(edited)</span>}
              {message.pinned && <Pin className="h-3 w-3 text-yellow-500 inline" />}
            </div>
          )}
          {compact && (
            <span className="text-[10px] text-zinc-600 opacity-0 group-hover:opacity-100 absolute -ml-8 mt-0.5 transition-opacity">{timeStr}</span>
          )}

          {/* Message content with basic markdown */}
          <div className="text-sm text-zinc-200 leading-relaxed break-words overflow-hidden" style={{ overflowWrap: 'anywhere' }}>
            <MarkdownText text={message.content} />
          </div>

          {embeds.length > 0 && (
            <div className="mt-2 space-y-2" data-testid={`message-embeds-${message.message_id}`}>
              {embeds.map((embed, idx) => (
                <div key={`${embed.type}-${idx}`} data-testid={`message-embed-${message.message_id}-${idx}`}>
                  {embed.type === 'image' && (
                    <img src={embed.url} alt="embed" className="max-w-full rounded-lg border border-white/10" />
                  )}
                  {embed.type === 'youtube' && (
                    <div className="w-full aspect-video rounded-lg overflow-hidden border border-white/10">
                      <iframe
                        title={embed.id}
                        src={`https://www.youtube.com/embed/${embed.id}`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                  {embed.type === 'link' && (
                    <a
                      href={embed.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-[#12141a] border border-white/10 rounded-lg p-3 hover:border-cyan-500/30 transition-colors"
                    >
                      <p className="text-xs text-zinc-500">{embed.domain}</p>
                      <p className="text-sm text-cyan-300 truncate" data-testid={`message-embed-link-${message.message_id}-${idx}`}>{embed.url}</p>
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Reactions */}
          {Object.keys(reactions).length > 0 && (
            <div className="flex gap-1 mt-1 flex-wrap">
              {Object.entries(reactions).map(([emoji, userIds]) => (
                <button
                  key={emoji}
                  onClick={() => {
                    if (!canUseMedia) {
                      onMediaBlocked?.();
                      return;
                    }
                    onReaction(emoji);
                  }}
                  disabled={!canUseMedia}
                  data-testid={`reaction-${message.message_id}-${emoji}`}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${
                    userIds.includes(currentUserId)
                      ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300'
                      : 'bg-white/5 border-white/5 text-zinc-400 hover:bg-white/10'
                  } ${!canUseMedia ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <span>{emoji}</span>
                  <span className="text-[10px] font-medium">{userIds.length}</span>
                </button>
              ))}
            </div>
          )}

          {/* Thread indicator */}
          {message.thread_id && (
            <button onClick={onThread} className="flex items-center gap-1.5 mt-1 text-xs text-cyan-400 hover:text-cyan-300 hover:underline" data-testid={`thread-indicator-${message.message_id}`}>
              <MessageSquare className="h-3 w-3" />
              <span>View thread</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Basic markdown renderer
const MarkdownText = ({ text }) => {
  if (!text) return null;

  // Process markdown patterns
  const parts = [];
  let remaining = text;
  let key = 0;

  // Code blocks ```...```
  const codeBlockRegex = /```([\s\S]*?)```/g;
  let lastIdx = 0;
  let match;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<InlineMarkdown key={key++} text={text.slice(lastIdx, match.index)} />);
    }
    parts.push(
      <pre key={key++} className="bg-[#1a1d23] border border-white/5 rounded-md px-3 py-2 my-1 text-xs font-mono overflow-x-auto">
        <code>{match[1].trim()}</code>
      </pre>
    );
    lastIdx = match.index + match[0].length;
  }

  if (lastIdx < text.length) {
    parts.push(<InlineMarkdown key={key++} text={text.slice(lastIdx)} />);
  }

  if (parts.length === 0) {
    parts.push(<InlineMarkdown key={0} text={text} />);
  }

  return <>{parts}</>;
};

const InlineMarkdown = ({ text }) => {
  // Process inline: `code`, **bold**, *italic*, __underline__, ~~strike~~, [link](url)
  const result = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|~~[^~]+~~|\[([^\]]+)\]\(([^)]+)\))/).map((part, i) => {
    if (!part) return null;
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="bg-[#1a1d23] border border-white/10 px-1 py-0.5 rounded text-xs font-mono text-cyan-300">{part.slice(1, -1)}</code>;
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-bold text-white">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
      return <em key={i} className="italic">{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('~~') && part.endsWith('~~')) {
      return <s key={i} className="line-through text-zinc-500">{part.slice(2, -2)}</s>;
    }
    return part;
  });

  return <span>{result}</span>;
};

const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const IMAGE_REGEX = /\.(png|jpe?g|gif|webp)$/i;

function extractEmbeds(text) {
  if (!text) return [];
  const urls = (text.match(URL_REGEX) || [])
    .map(url => url.replace(/[),.]+$/, ''));
  const unique = Array.from(new Set(urls));

  return unique.map(url => {
    const youtubeId = getYoutubeId(url);
    if (youtubeId) return { type: 'youtube', url, id: youtubeId };
    if (IMAGE_REGEX.test(url)) return { type: 'image', url };
    return { type: 'link', url, domain: getDomain(url) };
  });
}

function getYoutubeId(url) {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) {
      return parsed.pathname.replace('/', '');
    }
    if (parsed.hostname.includes('youtube.com')) {
      return parsed.searchParams.get('v');
    }
    return null;
  } catch {
    return null;
  }
}

function getDomain(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}

function getRoleColor(roles) {
  if (!roles || roles.length === 0) return '#e4e4e7';
  if (roles.includes('role_owner')) return '#FFD700';
  if (roles.includes('role_admin')) return '#E74C3C';
  if (roles.includes('role_mod')) return '#3498DB';
  if (roles.includes('role_vip')) return '#9B59B6';
  if (roles.includes('role_active')) return '#E67E22';
  return '#e4e4e7';
}


function groupMessagesByDate(messages) {
  const groups = [];
  let current = null;
  for (const msg of messages) {
    const date = new Date(msg.created_at).toLocaleDateString();
    if (!current || current.date !== date) {
      current = { date, messages: [] };
      groups.push(current);
    }
    current.messages.push(msg);
  }
  return groups;
}

function timeDiffMinutes(a, b) {
  return Math.abs(new Date(b) - new Date(a)) / 60000;
}
